<template>
  <v-row>
    <v-col>
      <v-data-table
        :headers="headers"
        :items="payouts"
        :server-items-length="totalPayouts"
        :loading="payoutsLoading"
        hide-default-footer
        :multi-sort="false"
        :must-sort="true"
        @page-count="pageCount = $event"
        :page.sync="page"
        :options.sync="options"
        :items-per-page="itemsPerPage"
        :footer-props="{ showFirstLastPage: true }"
        class="ic-table elevation-0 ic-table--heavy-headers"
      >
        <template v-slot:top>
          <v-container fluid class="white header-container">
            <v-row align="center" dense justify="center">
              <v-col cols="12" md="4" class="v-toolbar__title">
                {{ $t("pages.payouts.list.title") }}
              </v-col>
              <v-col cols="12" md="8">
                <div class="d-flex flex-wrap align-center justify-end toolbar-items">
                  <date-range-picker
                    :locale="selectedLanguage"
                    @rangeChanged="onDateRangeChanged"
                    :btn-props="{ outlined: true,  }"
                    :parentStart="formatDate(statusAtStart)"
                    :parentEnd="formatDate(statusAtEnd)"
                  >
                  </date-range-picker>
                  <v-btn
                      color="primary"
                      :loading="reportGenerating"
                      @click="downloadPayoutReport()"
                      class="toolbar-item header-list-button"
                    >
                      <span>{{ $t("pages.dispute.list.download") }}</span>
                    </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div class="header-separator"></div>
        </template>
        <template v-slot:header.statusAt="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.amount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.bankName="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:item.statusAt="{ item }">
          <span>{{ formatDate(item.statusAt) }}</span>
        </template>
        <template v-slot:item.amount="{ item }">
          <span>{{ formattedAmount(item.amount) }}</span>
        </template>

        <template v-slot:item.bankName="{ item }">
          <span>{{ item.bankName }}</span>
        </template>
        <template v-slot:item.action="{ item }">
          <payouts-charges-modal
            :charges="charges"
            :chargesLoading="chargesLoading"
            :totalCharges="totalCharges"
            :payout="item"
            @getChargesForPayout="getChargesForPayout($event)"
            :currencySymbol="currencySymbol"
            @downloadReport="downloadChargeReport($event)"
            :reportGenerating="reportGenerating"
          >
          </payouts-charges-modal>
        </template>

      </v-data-table>
      <div class="text-center pt-8">
        <v-pagination v-model="page" :length="pageCount" total-visible="10"></v-pagination>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import debounce from 'debounce';
import moment from 'moment';
import dayjs from '../plugins/dayjs';
import DateRangePicker from './elements/DateRangePicker.vue';
import PayoutsChargesModal from './PayoutsChargesModal.vue';

export default {
  name: 'payoutList',
  mixins: [],
  components: {
    DateRangePicker,
    PayoutsChargesModal,
  },
  props: {
    payoutsLoading: {
      type: Boolean,
      required: true,
    },
    loadingPayouts: {
      type: Boolean,
      required: true,
    },
    payouts: {
      type: Array,
      required: true,
    },
    totalPayouts: {
      type: Number,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    currencySymbol: {
      type: String,
    },
    chargesLoading: {
      type: Boolean,
      required: true,
    },
    charges: {
      type: Array,
      required: true,
    },
    totalCharges: {
      type: Number,
      required: true,
    },
    reportGenerating: {
      type: Boolean,
      required: true,
    },
  },
  created() {
    this.debouncedLoad = debounce(this.load, 500);
  },
  mounted() {
    this.load();
  },
  data() {
    return {
      screenName: 'payouts-list',
      options: {
        sortBy: ['statusAt'], sortDesc: [true],
      },
      dialogs: {},
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      loading: false,
      status: 'paid',
      statusAtStart: dayjs()
        .subtract(31, 'days')
        .format('YYYY-MM-DD'),
      statusAtEnd: dayjs().format('YYYY-MM-DD'),
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    totalPayouts() {
      this.options.pageCount = Math.round(this.totalPayouts / this.itemsPerPage) + 1;
    },
    statusAtStart() {
      this.load();
    },
    statusAtEnd() {
      this.load();
    },
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('pages.payouts.list.statusAt'),
          align: 'left',
          sortable: true,
          value: 'statusAt',
        },
        {
          text: this.$t('pages.payouts.list.amount'),
          align: 'left',
          sortable: true,
          value: 'amount',
        },
        {
          text: this.$t('pages.payouts.list.bankName'),
          align: 'left',
          sortable: true,
          value: 'bankName',
        },
        {
          text: '',
          value: 'action',
          width: '10%',
          sortable: false,
          align: 'end',
        },
      ];
    },
    statusAtStartForRequest() {
      return `${this.statusAtStart}T00:00:00`;
    },
    statusAtEndForRequest() {
      return `${this.statusAtEnd}T23:59:59`;
    },
  },
  methods: {
    async load() {
      if (moment(this.statusAtEnd).diff(moment(this.statusAtStart), 'days') > 31) {
        this.$store.dispatch('globalMessages/addError',
          { message: 'lists.dateRangeError' });
        return;
      }
      const { page, sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'createdAt';
      this.$emit('loadData', {
        page: page - 1,
        statusAtStart: this.statusAtStartForRequest,
        statusAtEnd: this.statusAtEndForRequest,
        status: this.status,
        maxPerPage: this.itemsPerPage,
        sort: sortDesc[0] ? `-${sort}` : sort,
      });
    },
    async getChargesForPayout({ page, maxPerPage, sort, payoutId }) {
      this.$emit('loadChargesForPayout', {
        page,
        maxPerPage,
        sort,
        payoutId,
      });
    },
    formatDate(date) {
      if (!date) return null;
      return dayjs(date).format('YYYY-MM-DD');
    },
    onDateRangeChanged(date) {
      this.statusAtStart = dayjs(date.start).format('YYYY-MM-DD');
      this.statusAtEnd = dayjs(date.end).format('YYYY-MM-DD');
    },
    formattedAmount(amount) {
      return amount ? `${this.currencySymbol}${amount}` : '';
    },
    downloadChargeReport({ sort, payoutId }) {
      this.$emit('downloadChargeReport', {
        sort,
        payoutId,
      });
    },
    downloadPayoutReport() {
      const { sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'statusAt';
      this.$emit('downloadPayoutReport', {
        sort: sortDesc[0] ? `-${sort}` : sort,
        statusAtStart: this.statusAtStartForRequest,
        statusAtEnd: this.statusAtEndForRequest,
      });
    },
  },
};
</script>

<style lang="scss">
.toolbar-items {
  .toolbar-item:not(:first-child) {
    margin-left: 8px;
    margin-bottom: 0;
  }
}

.capture-date-picker {
  display: flex;
  flex-flow: row;
  border: 1px #a1a1a1 solid;
  border-radius: 4px;
  padding: 4px 12px;
  max-height: 40px;
  align-items: center;
}

.mobile-title-capture {
  display: flex;
  justify-content: space-between;

  .header-list-button {
    padding: 0 !important;
    min-width: 45px !important;
  }
}

.capture-header-menu {
  display: flex;
  flex-flow: row;
  align-items: center;
}

.date-picker-icon-arrow {
  min-width: 20px;
  min-height: 20px;
}

.date-picker-icon-calendar {
  width: 30px;
  height: 30px;
  min-width: 20px;
  min-height: 20px;
}

@media only screen and (max-width: 1273px) {
  .date-picker-icon-calendar {
    min-width: 20px;
    min-height: 20px;
    width: 20px;
    height: 20px;
  }
}

@media only screen and (max-width: 959px) {
  .capture-header-menu-input {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}
</style>

<style scoped lang="scss">
.header-list-button {
  height: 36px !important;
}
</style>
